import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useStore } from '../../common/Context'
import useAuth from '../../common/hooks/useAuth'
import useToast from '../../common/hooks/useToast'
import useModal from '../../common/hooks/useModal'
import ConfirmModalContent from '../../components/common/ConfirmModalContent'
import useError from '../../common/hooks/useError'
import fetchAPI from '../../common/fetchAPI'
import ActionsDropdown from '../../components/common/ActionsDropdown'
import { ICONS } from '../../common/constants'
import Toggle from '../../components/common/Toggle'
import MembershipInfo from './MembershipInfo'
import AccountInfo from './AccountInfo'
import BillingMethodInfo from './BillingMethodInfo'
import BillingData from './BillingData'
import Notes from './Notes'
import AccessInfo from './AccessInfo'
import UserTasks from './UserTasks'
import useData from '../../common/hooks/useData'
import TeamInfo from './TeamInfo'
import { AssignedUsersToggle } from './AssignedUsersToggle'
import Timesheets from './Timesheets'
import MessageSection from '../../components/common/MessageSection'
import authStorage from '../../common/authStorage'
import Spinner from '../../components/common/Spinner'
import ErrorMessage from '../../components/common/ErrorMessage'
import Tag from '../../components/common/Tag'
import Button from '../../components/common/Button'
import BlockStack from '../../components/common/BlockStack'
import InlineStack from '../../components/common/InlineStack'
import InlineStackItem from '../../components/common/InlineStackItem'
import FavoriteInfo from './FavoriteInfo'
import SectionContainer from '../../components/common/SectionContainer'
import Divider from '../../components/common/Divider'
import Break from '../../components/common/Break'
import Chat from '../../components/common/Chat'
import ContactThumbnail from '../admin/clients/ContactThumbnail'
import CompanyInfo from './CompanyInfo'
import BasicInfo from './BasicInfo'
import PremiumBanner from '../admin/app-billing/PremiumBanner'
import useMatchMutate from '../../common/hooks/useMatchMutate'
import ContactCard from '../admin/clients/ContactCard'
import { getUserDisplayName } from '../../common/helpers'
import ProfileBillingData from './ProfileBillingData'
import useUrlMessage from '../../common/hooks/useUrlMessage'

const permissions = {
    user: {
        admin: {
            fields: 'avatarUrl,firstName,lastName,email,onHold,onBlackList,isBlocked,onCollections,jobType,teamRoles,defaultHourlyRate,assignedClientIds,labels,billingIds,isPremiumUser,role,notes,externalNotes,internalNotes,lastAccountAccessDate,lastPasswordResetDate,accounts,isPaid,aboutHtml',
            populate:
                'assignedClients,contactDetails,defaultOrgDefaultInfo,allCompaniesMemberInfo,allCompaniesMember,allCompanyOrgs.$name,allCompanyOrgs.$acronym,allCompanyOrgs.$logoUrl',
        },
        assistant: {
            fields: 'avatarUrl,firstName,lastName,email,onHold,onBlackList,isBlocked,onCollections,jobType,teamRoles,defaultHourlyRate,assignedClientIds,labels,billingIds,isPremiumUser,role,notes,externalNotes,internalNotes,lastAccountAccessDate,lastPasswordResetDate,accounts,isPaid,aboutHtml',
            populate:
                'assignedClients,contactDetails,defaultOrgDefaultInfo,allCompaniesMemberInfo,allCompaniesMember,allCompanyOrgs.$name,allCompanyOrgs.$acronym,allCompanyOrgs.$logoUrl',
        },
        sales: {
            fields: 'avatarUrl,firstName,lastName,email,onHold,onBlackList,isBlocked,onCollections,labels,billingIds,isPremiumUser,role,notes,externalNotes,internalNotes,accounts,aboutHtml',
            populate:
                'contactDetails,defaultOrgDefaultInfo,allCompaniesMemberInfo,allCompaniesMember,allCompanyOrgs.$name,allCompanyOrgs.$acronym,allCompanyOrgs.$logoUrl',
        },
        collections: {
            fields: 'avatarUrl,firstName,lastName,email,onHold,onBlackList,isBlocked,onCollections,labels,billingIds,isPremiumUser,role,notes,externalNotes,internalNotes,accounts,aboutHtml',
            populate:
                'contactDetails,defaultOrgDefaultInfo,allCompaniesMemberInfo,allCompaniesMember,allCompanyOrgs.$name,allCompanyOrgs.$acronym,allCompanyOrgs.$logoUrl',
        },
        staff: {
            fields: 'avatarUrl,firstName,lastName,email,onHold,onBlackList,isBlocked,onCollections,labels,isPremiumUser,role,notes,externalNotes,internalNotes,accounts,teamRoles,aboutHtml',
            populate:
                'contactDetails,defaultOrgDefaultInfo,allCompaniesMemberInfo,allCompaniesMember,allCompanyOrgs.$name,allCompanyOrgs.$acronym,allCompanyOrgs.$logoUrl',
        },
    },
    contact: {
        all: {
            fields: 'avatarUrl,firstName,labels,lastName,email,notes,companyName,aboutHtml',
            populate: 'contactDetails,addresses,metafields',
        },
    },
}

function getFieldPermissions(auth, isContact) {
    let fields
    let populate
    if (isContact) {
        fields = permissions.contact.all.fields
        populate = permissions.contact.all.populate
    } else if (auth.isAdmin) {
        fields = permissions.user.admin.fields
        populate = permissions.user.admin.populate
    } else if (auth.isAssistant) {
        fields = permissions.user.assistant.fields
        populate = permissions.user.assistant.populate
    } else if (auth.isSales) {
        fields = permissions.user.sales.fields
        populate = permissions.user.sales.populate
    } else if (auth.isCollections) {
        fields = permissions.user.collections.fields
        populate = permissions.user.collections.populate
    } else if (auth.isStaff) {
        fields = permissions.user.staff.fields
        populate = permissions.user.staff.populate
    }
    return { fields, populate }
}

export default function ProfilePage() {
    const [_state, dispatch] = useStore()
    const auth = useAuth()
    const { setModal, setModalLoading, isModalLoading } = useModal()
    const setToast = useToast()
    const matchMutate = useMatchMutate()
    const { contactId, userId } = useParams()
    const [searchParams] = useSearchParams()
    const tagFromParams = searchParams.get('tag_task_list')

    const isOwnProfile = !contactId && !userId
    const isContact = Boolean(contactId)

    let { fields, populate } = getFieldPermissions(auth, isContact)

    let url = ''
    if (isContact) {
        url = `/v1/contacts/${contactId}?fields=${fields}${
            populate ? `&populate=${populate}` : ''
        }`
    } else if (isOwnProfile) {
        url = `/v1/users/${auth.user.id}?fields=avatarUrl,firstName,lastName,email,billingIds,isPremiumUser,role${auth.isStaff ? ',accounts,teamRoles,assignedClientIds' : ''}&populate=${auth.isStaff ? 'assignedClients,' : ''}contactDetails,defaultOrgDefaultInfo,allCompaniesMemberInfo,allCompaniesMember,allCompanyOrgs.$name,allCompanyOrgs.$logoUrl,allCompanyOrgs.$acronym`
    } else {
        url = `/v1/users/${userId}?fields=${fields}${
            populate ? `&populate=${populate}` : ''
        }`
    }
    const { user, userError, userLoading, userMutate } = useData(url, 'user')

    let editUrl
    if (isContact) {
        editUrl = `/contacts/${contactId || userId || auth.user.id}/edit?type=contact`
    } else if (user?.role === 'staff') {
        editUrl = `/users/${contactId || userId || auth.user.id}/edit?type=staff${isOwnProfile ? '&isOwnProfile=true' : ''}`
    } else {
        editUrl = `/users/${contactId || userId || auth.user.id}/edit?type=user${isOwnProfile ? '&isOwnProfile=true' : ''}`
    }

    const createdMessageHtml = useUrlMessage(
        'created_message',
        <>
            Created new contact.&nbsp;<Link to={editUrl}>Edit</Link>.&nbsp;
            <Link to={'/contacts/new'}>Create another.</Link>
        </>,
    )

    const [error, setError] = useError()
    const navigate = useNavigate()

    async function onConvertToUser() {
        setError('')
        setModal(
            <ConfirmModalContent
                text={
                    'Converting to an app user will not send an email. You can do this via client Settings. If the contact has a company, one will be created or edited to include them as a member.'
                }
                onConfirm={async () => {
                    if (isModalLoading) return
                    setModalLoading(true)

                    const { responseData, error } = await fetchAPI(
                        `/v1/contacts/${user.id}/convert-to-user`,
                        {},
                        'POST',
                    )
                    if (error) {
                        setModal(null)
                        setError(error)
                        return
                    }
                    setModal(null)
                    setToast('Done')
                    navigate(`/users/${responseData.userId}`)
                }}
            />,
            'Convert to app user?',
        )
    }
    async function onHoldChange() {
        setError('')
        setModal(
            <ConfirmModalContent
                onConfirm={async () => {
                    if (isModalLoading) return
                    setModalLoading(true)
                    const actionUrl = user.onHold
                        ? `/v1/payment/clients/${user.id}/revert-on-hold`
                        : `/v1/payment/clients/${user.id}/on-hold`

                    const { responseData, error } = await fetchAPI(
                        actionUrl,
                        {},
                        'POST',
                    )
                    if (error) {
                        setModal(null)
                        setError(error)
                        return
                    }
                    setModal(null)
                    setToast('Done')
                    userMutate({ ...user, ...responseData })
                }}
            />,
            "Are you sure you want to change this user's on-hold status?",
        )
    }

    async function onBlackListChange() {
        setError('')
        setModal(
            <ConfirmModalContent
                onConfirm={async () => {
                    if (isModalLoading) return
                    setModalLoading(true)
                    const actionUrl = user.onBlackList
                        ? `/v1/payment/clients/${user.id}/revert-on-blacklist`
                        : `/v1/payment/clients/${user.id}/on-blacklist`

                    const { responseData, error } = await fetchAPI(
                        actionUrl,
                        {},
                        'POST',
                    )
                    if (error) {
                        setModal(null)
                        setError(error)
                        return
                    }
                    setModal(null)
                    setToast('Done')
                    userMutate({ ...user, ...responseData })
                }}
            />,
            'Are you sure you want to change this user’s blacklist status?',
        )
    }

    async function onContactBlackListChange() {
        const { responseData, error } = await fetchAPI(
            `/v1/contacts/${contactId}`,
            {
                onBlackList: !user.onBlackList,
            },
            'PATCH',
        )
        if (error) return setError(error)
        userMutate({ ...user, ...responseData })
        setToast('Done')
    }

    async function onCollectionsChange() {
        setError('')
        setModal(
            <ConfirmModalContent
                onConfirm={async () => {
                    if (isModalLoading) return
                    setModalLoading(true)
                    const actionUrl = user.onCollections
                        ? `/v1/payment/clients/${user.id}/revert-on-collections`
                        : `/v1/payment/clients/${user.id}/on-collections`

                    const { responseData, error } = await fetchAPI(
                        actionUrl,
                        {},
                        'POST',
                    )
                    if (error) {
                        setModal(null)
                        setError(error)
                        return
                    }
                    setModal(null)
                    setToast('Done')
                    userMutate({ ...user, ...responseData })
                }}
            />,
            'Are you sure you want to change this user’s collections status?',
        )
    }
    async function onToggleBlock() {
        const { responseData, error } = await fetchAPI(
            `/v1/users/${user.id}`,
            {
                isBlocked: !user.isBlocked,
            },
            'PATCH',
        )
        if (error) return setError(error)
        userMutate({ ...user, ...responseData })
        setToast('Done')
    }

    async function onLoginAsUser() {
        const { responseData, error } = await fetchAPI(
            `/v1/auth/login-as-user`,
            {
                userId: user.id,
            },
            'POST',
        )

        if (error) return setError(error)

        const { user: responseUser, tokens } = responseData
        authStorage.setItem('access_token', tokens.access.token)
        authStorage.setItem('refresh_token', tokens.refresh.token)

        dispatch({ type: 'LOGIN', payload: responseUser })
        navigate('/')
        window.location.reload()
    }

    function onChat() {
        setModal(
            <Chat roomType={'direct'} otherUserIds={[user.id]} />,
            getUserDisplayName(user),
            'modal-full',
        )
    }

    async function handleInvite() {
        setModal(
            <ConfirmModalContent
                onConfirm={async () => {
                    if (isModalLoading) return
                    setModalLoading(true)
                    const { error } = await fetchAPI(
                        `/v1/users/invite`,
                        {
                            email: user.email,
                            contactId,
                        },
                        'POST',
                    )
                    setModalLoading(false)
                    if (error) {
                        setToast(error, 'alert')
                        setModal(null)
                        return
                    }
                    setModal(null)
                    setToast('Invite sent')
                    matchMutate(/\/v1\/users\/invites*/)
                }}
            />,
            'Send a connection invite?',
        )
    }

    if (userLoading) {
        return <Spinner />
    }
    if (userError) {
        return <ErrorMessage section>{userError}</ErrorMessage>
    }

    if (isOwnProfile && !user) {
        return <ErrorMessage section>Unauthenticated</ErrorMessage>
    }

    const dropdownActions = [
        {
            title: 'Edit',
            onClick: () => navigate(editUrl),
            hide: !auth.isAdmin && !auth.isAssistant,
            icon: ICONS.EDIT_GRAY,
        },
        {
            title: 'Chat',
            onClick: () => onChat(),
            hide: isContact,
            icon: ICONS.MESSAGE_SQUARE_GRAY,
        },
        {
            title: user.onHold ? 'Clear hold' : 'Place on hold',
            onClick: () => onHoldChange(),
            hide: !auth.isAdmin || isContact,
        },
        {
            title: user.onBlackList
                ? 'Remove from blacklist'
                : 'Add to blacklist',
            onClick: () => onBlackListChange(),
            hide: (!auth.isAdmin && !auth.isClientManager) || isContact,
        },
        {
            title: user.onCollections
                ? 'Remove from collections'
                : 'Add to collections',
            onClick: () => onCollectionsChange(),
            hide: (!auth.isAdmin && !auth.isClientManager) || isContact,
        },
        {
            title: user.isBlocked ? 'Unblock' : 'Block',
            onClick: () => onToggleBlock(),
            hide: (!auth.isAdmin && !auth.isAssistant) || isContact,
            icon: ICONS.SLASH_RED,
            type: 'alert',
        },
        {
            title: 'Settings',
            onClick: () => navigate(`/users/${userId}/settings`),
            hide: (!auth.isAdmin && !auth.isAssistant) || isContact,
            icon: ICONS.SETTINGS_GRAY,
        },
        {
            title: 'Login as this user',
            onClick: () => onLoginAsUser(),
            hide: (!auth.isAdmin && !auth.isClientManager) || isContact,
            icon: ICONS.LOGIN_GRAY,
        },
        {
            title: 'Metafields',
            onClick: () =>
                navigate(
                    `/${contactId ? 'contacts' : 'users'}/${
                        contactId || userId
                    }/metafields`,
                ),
            hide: !auth.isAdmin && !isContact,
        },
        {
            title: 'Share',
            onClick: () =>
                navigator.share({
                    title: 'Share',
                    text: `aCo app: ${user.firstName} ${user.lastName}`,
                    url: `${window.location.origin}/${
                        contactId ? 'contacts' : 'users'
                    }/${contactId || userId}`,
                }),
        },
    ]

    const canSeeCollectionsInfo = auth.isAdmin || auth.isCollections
    const canSeeContactInfo =
        isOwnProfile ||
        auth.isAdmin ||
        auth.isAssistant ||
        auth.isSales ||
        Boolean(contactId)

    return (
        <>
            {error && (
                <SectionContainer>
                    <ErrorMessage onDismiss={() => setError('')}>
                        {error}
                    </ErrorMessage>
                </SectionContainer>
            )}
            <SectionContainer>
                <header className="info-header client-page-info-header">
                    <BlockStack gap={'tiny'}>
                        <InlineStack spaceBetween>
                            <ContactCard withDetails user={user} />

                            <InlineStack>
                                {!isOwnProfile && (
                                    <FavoriteInfo
                                        type={isContact ? 'Lead' : 'User'}
                                        id={userId || contactId}
                                    />
                                )}
                                {isOwnProfile && (
                                    <ActionsDropdown
                                        actions={[
                                            {
                                                title: 'Edit',
                                                onClick: () =>
                                                    navigate(editUrl),
                                                icon: ICONS.EDIT_GRAY,
                                            },
                                            {
                                                title: 'Share',
                                                onClick: () =>
                                                    navigator.share({
                                                        title: 'Share',
                                                        text: `aCo app: ${user.firstName} ${user.lastName}`,
                                                        url: `${window.location.origin}/${
                                                            contactId
                                                                ? 'contacts'
                                                                : 'users'
                                                        }/${contactId || userId}`,
                                                    }),
                                            },
                                        ]}
                                    />
                                )}
                                {!auth.isClient &&
                                    !isContact &&
                                    !isOwnProfile && (
                                        <ActionsDropdown
                                            actions={dropdownActions}
                                        />
                                    )}
                                {(auth.isStaff || auth.isClient) &&
                                    isContact && (
                                        <ActionsDropdown
                                            actions={[
                                                ...(auth.isClient
                                                    ? [
                                                          {
                                                              title: 'Invite',
                                                              onClick:
                                                                  handleInvite,
                                                              getHide: (item) =>
                                                                  item.role ||
                                                                  item.organizationType ||
                                                                  !item.email,
                                                          },
                                                      ]
                                                    : []),
                                                {
                                                    title: 'Edit',
                                                    onClick: () =>
                                                        navigate(editUrl),
                                                    icon: ICONS.EDIT_GRAY,
                                                },
                                                ...(auth.isClient
                                                    ? [
                                                          {
                                                              title: 'Place on hold',
                                                              onClick: () =>
                                                                  navigate(
                                                                      `/contacts/${contactId}/contact-request-feature?requestFeature=place_contacts_on_hold`,
                                                                  ),
                                                          },
                                                          {
                                                              title: 'Add to blacklist',
                                                              onClick: () =>
                                                                  navigate(
                                                                      `/contacts/${contactId}/contact-request-feature?requestFeature=add_contacts_to_blacklist`,
                                                                  ),
                                                          },

                                                          {
                                                              title: 'Block',
                                                              onClick: () =>
                                                                  navigate(
                                                                      `/contacts/${contactId}/contact-request-feature?requestFeature=block_contacts`,
                                                                  ),
                                                              icon: ICONS.SLASH_RED,
                                                              type: 'alert',
                                                          },
                                                          {
                                                              title: 'Settings',
                                                              onClick: () =>
                                                                  navigate(
                                                                      `/contacts/${contactId}/request-feature?requestFeature=manage_contact_settings&message=${encodeURIComponent(
                                                                          'Manage settings such as account info, vendor features, social posting, and more.',
                                                                      )}`,
                                                                  ),
                                                              icon: ICONS.SETTINGS_GRAY,
                                                          },
                                                      ]
                                                    : []),
                                                {
                                                    title: 'Share',
                                                    onClick: () =>
                                                        navigator.share({
                                                            title: 'Share',
                                                            text: `aCo app: ${user.firstName} ${user.lastName}`,
                                                            url: `${window.location.origin}/${
                                                                contactId
                                                                    ? 'contacts'
                                                                    : 'users'
                                                            }/${contactId || userId}`,
                                                        }),
                                                },
                                            ]}
                                        />
                                    )}
                                {auth.isAdmin && isContact && !isOwnProfile && (
                                    <ActionsDropdown
                                        actions={[
                                            {
                                                title: 'Edit',
                                                onClick: () =>
                                                    navigate(editUrl),
                                                hide:
                                                    !auth.isAdmin &&
                                                    !auth.isAssistant,
                                                icon: ICONS.EDIT_GRAY,
                                            },
                                            {
                                                title: 'Convert to user',
                                                onClick: () =>
                                                    onConvertToUser(),
                                                hide:
                                                    !auth.isAdmin || !isContact,
                                            },
                                            {
                                                title: user.onBlackList
                                                    ? 'Remove from blacklist'
                                                    : 'Add to blacklist',
                                                onClick: () =>
                                                    onContactBlackListChange(),
                                            },
                                            {
                                                title: 'Share',
                                                onClick: () =>
                                                    navigator.share({
                                                        title: 'Share',
                                                        text: `aCo app: ${user.firstName} ${user.lastName}`,
                                                        url: `${window.location.origin}/${
                                                            contactId
                                                                ? 'contacts'
                                                                : 'users'
                                                        }/${contactId || userId}`,
                                                    }),
                                            },
                                        ]}
                                    />
                                )}
                            </InlineStack>
                        </InlineStack>

                        <InlineStack gap={'sm'} overflowHidden itemsCenter>
                            <InlineStackItem shrink0>
                                {user.email && (
                                    <div className="text-secondary small">
                                        <Button
                                            icon={ICONS.MAIL_GRAY}
                                            link
                                            plain
                                            inline
                                            className={'a'}
                                            tiny
                                            href={`mailto:${user.email}`}
                                            text={user.email}
                                        />
                                    </div>
                                )}
                            </InlineStackItem>
                            <InlineStackItem shrink0>
                                {user.contactDetails?.phone && (
                                    <div className="text-secondary small">
                                        <Button
                                            icon={ICONS.PHONE_GRAY}
                                            link
                                            plain
                                            inline
                                            className={'a'}
                                            tiny
                                            href={`tel:${user.contactDetails.phone}`}
                                            text={user.contactDetails.phone}
                                        />
                                    </div>
                                )}
                            </InlineStackItem>
                        </InlineStack>

                        <InlineStack wrap gap={'tiny'}>
                            {user.isPremiumUser && (
                                <Tag color="gold" noMargin outline>
                                    Premium
                                </Tag>
                            )}
                            {(auth.isAdmin || auth.isAssistant) &&
                            user.isPaid ? (
                                <Tag noMargin color={'success'}>
                                    Paid
                                </Tag>
                            ) : null}
                            {(auth.isAdmin || auth.isAssistant) &&
                            !user.isPaid &&
                            !isContact ? (
                                <Tag noMargin outline>
                                    Free
                                </Tag>
                            ) : null}
                            {canSeeCollectionsInfo &&
                                (user.onHold ||
                                    user.onBlackList ||
                                    user.isBlocked ||
                                    user.onCollections) && (
                                    <>
                                        {user.onHold ? (
                                            <Tag noMargin color="alert">
                                                Hold
                                            </Tag>
                                        ) : null}
                                        {user?.onBlackList ? (
                                            <Tag noMargin color="alert">
                                                Blacklist
                                            </Tag>
                                        ) : null}
                                        {user.isBlocked ? (
                                            <Tag noMargin color="alert">
                                                Blocked
                                            </Tag>
                                        ) : null}
                                        {user.onCollections ? (
                                            <Tag noMargin color="alert">
                                                Collections
                                            </Tag>
                                        ) : null}
                                    </>
                                )}
                            {['friend', 'family', 'client'].includes(
                                user.contactType,
                            ) && (
                                <>
                                    {user.contactType === 'friend' && (
                                        <Tag noMargin color={'primary'}>
                                            Friend
                                        </Tag>
                                    )}
                                    {user.contactType === 'family' && (
                                        <Tag noMargin color={'primary'}>
                                            Family
                                        </Tag>
                                    )}
                                    {user.contactType === 'client' && (
                                        <Tag noMargin color={'primary'}>
                                            Client
                                        </Tag>
                                    )}
                                </>
                            )}
                            {!auth.isClient &&
                                !isOwnProfile &&
                                user.labels.map((l, i) => (
                                    <Tag
                                        key={i}
                                        noMargin
                                        color={
                                            l === 'active' ? 'success' : 'white'
                                        }
                                        outline
                                    >
                                        {l}
                                    </Tag>
                                ))}
                        </InlineStack>
                    </BlockStack>
                </header>
            </SectionContainer>
            <Divider />

            {createdMessageHtml && (
                <SectionContainer>{createdMessageHtml}</SectionContainer>
            )}

            {/* {auth.isClient &&
                isOwnProfile &&
                !profileCompletionValidating &&
                !profileCompletionLoading &&
                profileCompletion?.completedCompanyOrgProductsQuestions ===
                    false && (
                    <SectionContainer>
                        <MessageSection type="info">
                            Please fill in some info about your products and
                            your brand.
                            <Button
                                small
                                outline
                                white
                                href={`/profile/products-questions/edit?organizationId=${profileCompletion?.completedCompanyOrgProductsQuestionsMissingOrgIds?.[0]}`}
                                text="Go to form"
                            />
                        </MessageSection>
                    </SectionContainer>
                )} */}
            {isOwnProfile && auth.isClient && !auth.isPremiumUser ? (
                <SectionContainer>
                    <MessageSection type="info">
                        Upgrade to Premium. $70/month.
                        <Break />
                        <Button
                            small
                            outline
                            white
                            href={`/profile/billing/purchase-premium`}
                            text="Upgrade now"
                        />
                    </MessageSection>
                </SectionContainer>
            ) : null}
            {user.role === 'staff' && !auth.isClient && (
                <SectionContainer>
                    <Toggle
                        open={!auth.isAdmin}
                        title="Team"
                        small
                        heading={'h2'}
                    >
                        <TeamInfo teamRoles={user.teamRoles} />
                    </Toggle>
                </SectionContainer>
            )}
            {user.role === 'staff' && (auth.isAdmin || auth.isAssistant) && (
                <SectionContainer>
                    <AssignedUsersToggle
                        userId={user.id}
                        assignedClientIds={user.assignedClientIds}
                        assignedClients={user.assignedClients}
                    />
                </SectionContainer>
            )}
            <SectionContainer>
                <Toggle
                    open={!auth.isAdmin}
                    title="Contact information"
                    small
                    heading={'h2'}
                    mainButton={{
                        functionality: 'EDIT',
                        href: editUrl,
                        label: 'Edit',
                    }}
                >
                    <BasicInfo
                        user={user}
                        userId={userId}
                        contactDetails={user.contactDetails}
                        contactId={contactId}
                        defaultOrgInfo={user.defaultOrgDefaultInfo}
                        canSeeContactInfo={canSeeContactInfo}
                    />
                </Toggle>
            </SectionContainer>
            <SectionContainer>
                <Toggle
                    open={!auth.isAdmin}
                    title="Company information"
                    small
                    heading={'h2'}
                    headerChildrenHtml={
                        <div className="company-info-toggle-thumbnails">
                            <InlineStack>
                                {(user.allCompanyOrgs || [])
                                    .slice(0, 5)
                                    .map((org, i) => (
                                        <ContactThumbnail
                                            key={i}
                                            data={org}
                                            isOrg
                                            small
                                        />
                                    ))}
                            </InlineStack>
                        </div>
                    }
                >
                    <CompanyInfo
                        user={user}
                        userId={userId}
                        contactId={contactId}
                        allCompaniesMember={user.allCompaniesMember}
                        allCompaniesMemberInfo={user.allCompaniesMemberInfo}
                        allCompanyOrgs={user.allCompanyOrgs}
                    />
                </Toggle>
            </SectionContainer>

            {(auth.isAdmin || auth.isStaff) && !isContact && (
                <SectionContainer>
                    <Toggle
                        title="Tasks"
                        small
                        heading={'h2'}
                        open={tagFromParams || undefined}
                        mainButton={{
                            functionality: 'CREATE',
                            href: `/users/${user.id}/tasks/new`,
                            label: 'Add task',
                        }}
                    >
                        <UserTasks userId={user.id} role={user.role} />
                    </Toggle>
                </SectionContainer>
            )}
            {isContact && auth.isClient && (
                <SectionContainer>
                    <Toggle title="Tasks" small heading={'h2'}>
                        <span className="text-subdued">
                            {`Assign tasks to or regarding this contact.`}
                        </span>
                        <PremiumBanner
                            requestFeature={'create_tasks_for_contact'}
                        />
                    </Toggle>
                </SectionContainer>
            )}
            {!isContact && auth.isClient && (
                <SectionContainer>
                    <Toggle
                        title="Tasks"
                        small
                        heading={'h2'}
                        open={tagFromParams || undefined}
                    >
                        <UserTasks userId={user.id} role={user.role} />
                    </Toggle>
                </SectionContainer>
            )}

            {user.role === 'user' && (auth.isAdmin || isOwnProfile) && (
                <SectionContainer>
                    <Toggle
                        title="Membership"
                        small
                        heading={'h2'}
                        gold={user.isPremiumUser}
                    >
                        {user.isPremiumUser ? 'Premium' : 'Free'}
                        <Break />

                        {isOwnProfile ? (
                            <>
                                <Link to="/profile/billing/premium">
                                    See subscription details →
                                </Link>
                            </>
                        ) : (
                            <MembershipInfo
                                billingIds={user.billingIds}
                                isPremiumUser={user.isPremiumUser}
                            />
                        )}
                        <Break />
                        {isOwnProfile && (
                            <div>
                                <Link to="/donate">Donations →</Link>
                            </div>
                        )}
                    </Toggle>
                </SectionContainer>
            )}
            {user.role === 'user' &&
                (isOwnProfile || auth.isAdmin || auth.isAssistant) && (
                    <SectionContainer>
                        <Toggle
                            title="Accounts"
                            open={!auth.isAdmin}
                            small
                            heading={'h2'}
                        >
                            <AccountInfo userId={userId} />
                        </Toggle>
                    </SectionContainer>
                )}
            {isContact && auth.isClient && (
                <SectionContainer>
                    <Toggle title="Accounts" small heading={'h2'}>
                        <span className="text-subdued">
                            {`Add accounts such as Vendor to this contact.`}
                        </span>
                        <PremiumBanner
                            requestFeature={'add_accounts_to_contacts'}
                        />
                    </Toggle>
                </SectionContainer>
            )}
            {(isContact ||
                isOwnProfile ||
                auth.isAdmin ||
                auth.isAssistant) && (
                <SectionContainer>
                    <Toggle title="Billing methods" small heading={'h2'}>
                        <BillingMethodInfo
                            userId={userId}
                            contactId={contactId}
                        />
                    </Toggle>
                </SectionContainer>
            )}
            {user.role === 'user' && (auth.isAdmin || auth.isCollections) && (
                <SectionContainer>
                    <Toggle title="Billing" small heading={'h2'}>
                        <BillingData />
                    </Toggle>
                </SectionContainer>
            )}
            {isOwnProfile && (
                <SectionContainer>
                    <Toggle title="Billing" small heading={'h2'}>
                        <ProfileBillingData />
                    </Toggle>
                </SectionContainer>
            )}
            {isContact && auth.isClient && (
                <SectionContainer>
                    <Toggle title="Billing" small heading={'h2'}>
                        <span className="text-subdued">
                            {`Manage invoices and subscriptions from Stripe.`}
                        </span>
                        <PremiumBanner
                            requestFeature={
                                'manage_contact_stripe_invoices_and_subs'
                            }
                        />
                    </Toggle>
                </SectionContainer>
            )}

            {user.role === 'staff' && (auth.isAdmin || auth.isAssistant) && (
                <SectionContainer>
                    <Toggle
                        open={!auth.isAdmin}
                        title="Timesheets"
                        small
                        heading={'h2'}
                    >
                        <Timesheets userId={user.id} />
                    </Toggle>
                </SectionContainer>
            )}

            {!(auth.isClient && isOwnProfile) && (
                <SectionContainer>
                    <Toggle title="Notes" small heading={'h2'}>
                        <Notes
                            id={user.id}
                            role={user.role}
                            notes={user.notes}
                            externalNotes={user.externalNotes}
                            internalNotes={user.internalNotes}
                        />
                    </Toggle>
                </SectionContainer>
            )}
            {(auth.isAdmin || auth.isAssistant || auth.isClientManager) &&
                !isContact && (
                    <SectionContainer>
                        <Toggle title="Access" small heading={'h2'}>
                            <AccessInfo
                                lastAccountAccessDate={
                                    user.lastAccountAccessDate
                                }
                                lastPasswordResetDate={
                                    user.lastPasswordResetDate
                                }
                            />
                        </Toggle>
                    </SectionContainer>
                )}
        </>
    )
}
